import type { ShellProduct } from 'continity-shared-shell';

/** A cross-portal link as supplied by the shell runtime config. */
export interface PortalLink {
  key: string;
  label: string;
  url: string;
}

/**
 * Builds the cross-portal product-switcher entries for the Phoenix root
 * (922 Entscheidung 8, Option B — ADO #1543): sibling portals from runtime config
 * FIRST, then the current Phoenix product LAST, highlighted (`current: true`) and
 * non-clickable.
 *
 * Kept as a pure, dependency-free function (the `ShellProduct` import is type-only)
 * so it is unit-testable without bootstrapping the root component — which would pull
 * in the shared-ui barrel (and its `chart.js` dependency) at runtime.
 *
 * Icons: harmonized product identity across both portals (ADO #1543) — Continity =
 * 'shield' (its sidebar identity icon), Phoenix = 'zap' (live/energy). Any other sibling
 * key falls back to the generic 'layers' (cross-product). The task originally asked for
 * 'grid'/'flame', but neither is a registered shared-ui IconName (they render a "?"
 * placeholder + runtime console error). See CLAUDE.md "Shared-UI ZUERST prüfen".
 */
export function buildPhoenixProducts(portalLinks: PortalLink[], useB2cSso: boolean): ShellProduct[] {
  const siblings: ShellProduct[] = portalLinks.map((link) => ({
    key: link.key,
    label: link.label,
    // B2C users enter the main portal through its dedicated auto-start route. That host
    // establishes its own PKCE/cookie session against the existing B2C SSO session.
    // DevAuth remains manual because host-only mock sessions cannot cross origins.
    url: link.key === 'continity'
      ? useB2cSso
        ? `${link.url.replace(/\/+$/, '')}/auth/start?returnUrl=${encodeURIComponent('/dashboard')}`
        : `${link.url.replace(/\/+$/, '')}/dashboard`
      : link.url,
    icon: link.key === 'continity' ? 'shield' : 'layers',
  }));
  return [...siblings, { key: 'phoenix', label: 'Phoenix', current: true, icon: 'zap' }];
}
